import { useQuery } from "@tanstack/react-query";
import { FC, ReactNode } from "react";
import { DateTime } from "luxon";
import styled from "styled-components";

import { api } from "@/utils/api";
import { Table, TableContent, Td, Th } from "@/components/Table";
import ScrollableTable from "@/components/ScrollableTable";
import Text from "@/components/Text";
import { ReactComponent as GoldMedal } from "@/assets/icons/gold-medal.svg";
import { ReactComponent as SilverMedal } from "@/assets/icons/silver-medal.svg";
import { ReactComponent as BronzeMedal } from "@/assets/icons/bronze-medal.svg";

const StyledTable = styled(Table)`
  th,
  td {
    font-weight: 700;
    &:first-child {
      min-width: 200px;
      padding-left: 24px;
      width: 200px;
      position: sticky;
      z-index: 1;
      background-color: ${({ theme }) => theme.colors.white};
      left: 0;
      top: 0;
      bottom: 0;
    }
    &:last-child {
      padding-right: 24px;
    }
  }
  td {
    padding: 16px;
  }
  td[data-wins="0"] {
    color: ${({ theme }) => theme.colors.gray};
  }
`;

const Wrap = styled.div`
  margin-bottom: 56px;
`;

export const TableHolder = styled.div`
  box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.08);
  border-radius: 4px;
  background-color: ${({ theme }) => theme.colors.white};
`;

const TableHeader = styled.div`
  padding: 24px;
`;

const Heading = styled(Text)`
  span {
    font-weight: 400;
  }
`;

export const ParticipantHolder = styled.div`
  display: flex;
  align-items: center;
  svg {
    margin-left: 6px;
    flex-shrink: 0;
  }
`;

export const StyledScrollableTable = styled(ScrollableTable)`
  border-radius: 0;
  box-shadow: none;
  border-top: solid 1px ${({ theme }) => theme.colors.grayscaleLight};
`;

interface Participant {
  name: string;
  user_id: number;
  avgCtr: number;
  wins: {
    [date: string]: number;
  };
}

interface Props {
  activePeriod: string;
  startDt: DateTime;
  endDt: DateTime;
  renderThs: () => ReactNode[];
  sequenceValue: {
    value: number | "All";
    type: string;
  };
}

const ScoreByWins: FC<Props> = ({
  activePeriod,
  startDt,
  endDt,
  renderThs,
  sequenceValue,
}) => {
  const { data, isLoading, isError } = useQuery(
    [
      "leaderboard_scores",
      { type: "wins", period: activePeriod, sequenceValue },
    ],
    () => {
      return api.get<never, { users: Participant[]; winners: number[] }>(
        "/v1/gamification/thumbnails/score-by-wins",
        {
          params: {
            date_from: activePeriod,
            date_to: DateTime.fromFormat(activePeriod, "yyyy-LL-dd")
              .endOf("month")
              .toFormat("yyyy-LL-dd"),
            ...(sequenceValue?.value !== "All" && {
              ...(sequenceValue?.type === "C"
                ? { c_batch: sequenceValue.value }
                : { batch: sequenceValue.value }),
            }),
          },
        }
      );
    }
  );

  const now = DateTime.now();

  const renderTds = (wins: Participant["wins"]) => {
    const components: ReactNode[] = [];

    let days = 0;

    while (startDt.plus({ days }).toFormat("dd") <= endDt.toFormat("dd")) {
      const dailyWins =
        wins[startDt.plus({ days }).toFormat("yyyy-LL-dd")] ??
        (startDt.plus({ days }) < now ? 0 : "-");
      components.push(
        <Td key={days} data-wins={dailyWins}>
          {dailyWins}
        </Td>
      );

      days++;

      if (days === parseInt(endDt.toFormat("dd"))) {
        break;
      }
    }

    return components;
  };

  const renderMedal = (userId: number) => {
    if (data?.winners.indexOf(userId) === 0) {
      return <GoldMedal />;
    }
    if (data?.winners.indexOf(userId) === 1) {
      return <SilverMedal />;
    }
    if (data?.winners.indexOf(userId) === 2) {
      return <BronzeMedal />;
    }
  };

  return (
    <Wrap>
      <TableHolder>
        <TableHeader>
          <Heading $type="smallBold">
            Participants Score by Wins{" "}
            <span>
              · {sequenceValue.type}
              {sequenceValue.value} ·{" "}
              {DateTime.fromISO(activePeriod).toFormat("yyyy LLLL")}
            </span>
          </Heading>
        </TableHeader>
        <StyledScrollableTable>
          <StyledTable>
            <thead>
              <tr>
                <Th>Participant</Th>
                {renderThs()}
              </tr>
            </thead>
            <tbody>
              <TableContent
                isLoading={isLoading}
                isError={isError}
                hasData={!!data?.users.length}
              >
                {data?.users.map((participant, index) => (
                  <tr key={index}>
                    <Td>
                      <ParticipantHolder>
                        {participant.name} {renderMedal(participant.user_id)}
                      </ParticipantHolder>
                    </Td>
                    {renderTds(participant.wins)}
                  </tr>
                ))}
              </TableContent>
            </tbody>
          </StyledTable>
        </StyledScrollableTable>
      </TableHolder>
    </Wrap>
  );
};

export default ScoreByWins;
